import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { 
  Link2, 
  Loader2, 
  RefreshCw,
  Trash2,
  Calendar,
  FileText,
  Building2,
  CreditCard,
  Wallet
} from 'lucide-react'
import api from '../services/api'
import { useLanguage } from '../hooks/useLanguage'
import { useToast } from '../hooks/useToast'

interface FICConnection {
  id: string
  company_id: number
  company_name: string
  connected_at: string
  last_sync: string | null
  status: 'active' | 'error' | 'expired'
}

export default function Integrations() {
  const { t } = useLanguage()
  const { confirm } = useToast()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isConnecting, setIsConnecting] = useState(false)
  const [syncingId, setSyncingId] = useState<string | null>(null)

  const handleOAuthCallback = useCallback(async (code: string, state: string) => {
    setIsConnecting(true)
    try {
      await api.post('/integrations/fattureincloud/callback', { code, state })
      queryClient.invalidateQueries({ queryKey: ['fic-connections'] })
    } catch (error) {
      console.error('OAuth callback error:', error)
    } finally {
      setIsConnecting(false)
    }
  }, [queryClient])

  // Handle FIC OAuth callback (skip if coming from TrueLayer)
  useEffect(() => {
    const code = searchParams.get('code')
    const state = searchParams.get('state')
    const error = searchParams.get('error')

    // Skip if this is a TrueLayer callback
    if (localStorage.getItem('truelayer_pending')) return

    if (code && state) {
      handleOAuthCallback(code, state)
      setSearchParams({})
    } else if (error) {
      console.error('OAuth error:', error)
      setSearchParams({})
    }
  }, [searchParams, handleOAuthCallback, setSearchParams])

  // Fetch connections
  const { data: connections, isLoading } = useQuery<FICConnection[]>({
    queryKey: ['fic-connections'],
    queryFn: async () => {
      try {
        const response = await api.get('/integrations/fattureincloud/connections')
        return response.data.connections || []
      } catch {
        return []
      }
    }
  })

  // ==================== TRUELAYER OPEN BANKING ====================
  const [isTrueLayerConnecting, setIsTrueLayerConnecting] = useState(false)
  const [isTrueLayerSyncing, setIsTrueLayerSyncing] = useState(false)

  // Check TrueLayer connection status
  const { data: trueLayerStatus } = useQuery({
    queryKey: ['truelayer-status'],
    queryFn: async () => {
      const response = await api.get('/integrations/truelayer/status')
      return response.data as { 
        connected: boolean
        has_credentials: boolean
        last_sync?: string
        accounts_count?: number
      }
    }
  })

  // Get TrueLayer accounts
  const { data: trueLayerAccounts } = useQuery({
    queryKey: ['truelayer-accounts'],
    queryFn: async () => {
      const response = await api.get('/integrations/truelayer/accounts')
      return response.data as {
        accounts: Array<{
          id: string
          name: string
          iban?: string
          type: string
          balance?: number
          currency?: string
        }>
        connected: boolean
      }
    },
    enabled: trueLayerStatus?.connected
  })

  // Handle TrueLayer callback from URL
  useEffect(() => {
    const isTrueLayerCallback = localStorage.getItem('truelayer_pending')
    if (!isTrueLayerCallback) return

    const code = searchParams.get('code')
    const trueLayerState = searchParams.get('state')
    
    if (code && trueLayerState) {
      localStorage.removeItem('truelayer_pending')
      setIsTrueLayerSyncing(true)
      api.post('/integrations/truelayer/callback', { 
        code, 
        state: trueLayerState 
      }).then(async () => {
        // Auto-sync transactions after successful callback
        try {
          const syncResponse = await api.post('/integrations/truelayer/sync')
          const synced = syncResponse.data?.transactions_synced ?? 0
          console.log('[TrueLayer] Sync result:', syncResponse.data)
          alert(`TrueLayer: sincronizzate ${synced} transazioni`)
          queryClient.invalidateQueries({ queryKey: ['truelayer-status'] })
          queryClient.invalidateQueries({ queryKey: ['truelayer-accounts'] })
          queryClient.invalidateQueries({ queryKey: ['transactions'] })
          queryClient.invalidateQueries({ queryKey: ['dashboard'] })
          queryClient.invalidateQueries({ queryKey: ['uploads'] })
        } catch (syncError) {
          console.error('Auto-sync error:', syncError)
          alert('Errore durante la sincronizzazione transazioni TrueLayer')
        }
      }).catch((err) => {
        console.error('TrueLayer callback error:', err)
        alert('Errore nel callback TrueLayer: ' + (err?.response?.data?.error || err.message))
      }).finally(() => {
        setIsTrueLayerSyncing(false)
      })
      
      setSearchParams({})
    }
  }, [searchParams, setSearchParams, queryClient])

  // Connect to TrueLayer
  const handleTrueLayerConnect = async () => {
    setIsTrueLayerConnecting(true)
    try {
      const response = await api.get('/integrations/truelayer/connect')
      if (response.data?.auth_url) {
        // Mark pending TrueLayer callback to avoid conflict with FIC useEffect
        localStorage.setItem('truelayer_pending', '1')
        window.location.href = response.data.auth_url
      }
    } catch (error) {
      console.error('TrueLayer connect error:', error)
      setIsTrueLayerConnecting(false)
    }
  }

  // Sync TrueLayer transactions
  const handleTrueLayerSync = async () => {
    setIsTrueLayerSyncing(true)
    try {
      const response = await api.post('/integrations/truelayer/sync')
      const synced = response.data?.transactions_synced ?? 0
      console.log('[TrueLayer] Manual sync result:', response.data)
      alert(`TrueLayer: sincronizzate ${synced} transazioni`)
      queryClient.invalidateQueries({ queryKey: ['truelayer-status'] })
      queryClient.invalidateQueries({ queryKey: ['truelayer-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['uploads'] })
    } catch (error: any) {
      console.error('TrueLayer sync error:', error)
      alert('Errore sync TrueLayer: ' + (error?.response?.data?.error || error.message))
    } finally {
      setIsTrueLayerSyncing(false)
    }
  }

  // Disconnect TrueLayer
  const disconnectTrueLayerMutation = useMutation({
    mutationFn: async () => {
      await api.post('/integrations/truelayer/disconnect')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['truelayer-status'] })
      queryClient.invalidateQueries({ queryKey: ['truelayer-accounts'] })
    }
  })

  // Connect mutation - get auth URL from backend
  const connectMutation = useMutation({
    mutationFn: async () => {
      const response = await api.get('/integrations/fattureincloud/auth')
      const { auth_url, state } = response.data
      
      // Store state for validation
      localStorage.setItem('fic_oauth_state', state)
      
      // Redirect to FIC OAuth
      window.location.href = auth_url
    }
  })

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: async (connectionId: string) => {
      await api.delete(`/integrations/fattureincloud/connections/${connectionId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fic-connections'] })
    }
  })

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: async (connectionId: string) => {
      setSyncingId(connectionId)
      const response = await api.post(`/integrations/fattureincloud/sync/${connectionId}`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fic-connections'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onSettled: () => {
      setSyncingId(null)
    }
  })

  return (
    <div className="space-y-6 pt-4">
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          {/* Page title now in top bar */}
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">
          {t.integrations.subtitle}
        </p>
      </div>

      {/* Integration Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        
        {/* Fatture in Cloud */}
        <div className="card relative overflow-hidden">
          {/* Status indicator */}
          {connections && connections.length > 0 && (
            <div className="absolute top-4 right-4">
              <span className="flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
            </div>
          )}

          <div className="flex items-start gap-4 mb-4">
            {/* FIC Logo */}
            <div className="w-12 h-12 rounded-lg bg-white dark:bg-slate-700 flex items-center justify-center flex-shrink-0 p-1.5 border border-gray-100 dark:border-slate-600">
              <svg viewBox="0 0 100 60" className="w-full h-full">
                {/* Stylized "ts" logo */}
                <defs>
                  <linearGradient id="ficGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#00a0dc"/>
                    <stop offset="50%" stopColor="#e91e63"/>
                    <stop offset="100%" stopColor="#ff9800"/>
                  </linearGradient>
                </defs>
                <text x="5" y="45" fontFamily="Arial, sans-serif" fontSize="42" fontWeight="bold" fill="url(#ficGrad1)">ts</text>
                <text x="52" y="28" fontFamily="Arial, sans-serif" fontSize="14" fontWeight="bold" fill="#00a0dc">Fatture</text>
                <text x="52" y="45" fontFamily="Arial, sans-serif" fontSize="14" fill="#00a0dc">in Cloud</text>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 dark:text-white">Fatture in Cloud</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Importa fatture, prima nota e corrispettivi
              </p>
            </div>
          </div>

          {/* Features list */}
          <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 mb-4">
            <li className="flex items-center gap-2">
              <FileText className="w-3 h-3 text-blue-500" />
              Prima nota (Cashbook)
            </li>
            <li className="flex items-center gap-2">
              <FileText className="w-3 h-3 text-green-500" />
              Fatture emesse e ricevute
            </li>
            <li className="flex items-center gap-2">
              <FileText className="w-3 h-3 text-purple-500" />
              Ricevute e corrispettivi
            </li>
          </ul>

          {/* Connected accounts */}
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : connections && connections.length > 0 ? (
            <div className="space-y-3 mb-4">
              {connections.map((conn) => (
                <div 
                  key={conn.id} 
                  className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm text-gray-900 dark:text-white">
                      {conn.company_name}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      conn.status === 'active' 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400'
                        : conn.status === 'expired'
                        ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'
                    }`}>
                      {conn.status === 'active' ? 'Connesso' : conn.status === 'expired' ? 'Scaduto' : 'Errore'}
                    </span>
                  </div>
                  
                  {conn.last_sync && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Ultimo sync: {new Date(conn.last_sync).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => syncMutation.mutate(conn.id)}
                      disabled={syncingId === conn.id}
                      className="flex-1 btn btn-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {syncingId === conn.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          Sincronizza
                        </>
                      )}
                    </button>
                    <button
                      onClick={async () => {
                        const confirmed = await confirm({
                          title: 'Disconnetti Account',
                          message: 'Vuoi disconnettere questo account di Fatture in Cloud?',
                          confirmText: 'Disconnetti',
                          type: 'warning'
                        })
                        if (confirmed) {
                          disconnectMutation.mutate(conn.id)
                        }
                      }}
                      className="btn btn-sm bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/50 dark:hover:bg-red-900 dark:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {/* Connect button */}
          <button
            onClick={() => connectMutation.mutate()}
            disabled={isConnecting || connectMutation.isPending}
            className={`w-full btn ${
              connections && connections.length > 0
                ? 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-gray-300'
                : 'btn-primary'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isConnecting || connectMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Connessione...
              </>
            ) : connections && connections.length > 0 ? (
              <>
                <Link2 className="w-4 h-4" />
                Aggiungi altro account
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4" />
                Connetti Fatture in Cloud
              </>
            )}
          </button>
        </div>

        {/* TrueLayer Open Banking */}
        <div className="card relative overflow-hidden">
          {/* Status indicator */}
          {trueLayerStatus?.connected && (
            <div className="absolute top-4 right-4">
              <span className="flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
            </div>
          )}

          <div className="flex items-start gap-4 mb-4">
            {/* TrueLayer Logo */}
            <div className="w-14 h-14 rounded-lg bg-[#0D2C6C] flex flex-col items-center justify-center flex-shrink-0 p-1.5">
              <svg viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-8">
                <text x="50" y="25" fill="white" fontSize="14" fontFamily="Arial, sans-serif" fontWeight="bold" textAnchor="middle">TrueLayer</text>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 dark:text-white">Open Banking</h3>
                <span className="px-2 py-0.5 text-xs font-medium bg-[#0D2C6C] text-white rounded-full">
                  TrueLayer
                </span>
                <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded-full">
                  Sandbox
                </span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Connetti il tuo conto bancario
              </p>
            </div>
          </div>

          {/* Features list */}
          <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 mb-4">
            <li className="flex items-center gap-2">
              <CreditCard className="w-3 h-3 text-blue-500" />
              Transazioni in tempo reale
            </li>
            <li className="flex items-center gap-2">
              <Wallet className="w-3 h-3 text-green-500" />
              Saldi aggiornati automaticamente
            </li>
            <li className="flex items-center gap-2">
              <Building2 className="w-3 h-3 text-purple-500" />
              Banche italiane ed europee
            </li>
          </ul>

          {/* Connected accounts */}
          {trueLayerStatus?.connected && trueLayerAccounts?.accounts && trueLayerAccounts.accounts.length > 0 && (
            <div className="space-y-2 mb-4">
              {trueLayerAccounts.accounts.map((account) => (
                <div 
                  key={account.id}
                  className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm text-gray-900 dark:text-white">
                        {account.name}
                      </span>
                      {account.iban && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {account.iban.replace(/(.{4})/g, '$1 ').trim()}
                        </p>
                      )}
                    </div>
                    {account.balance !== undefined && (
                      <span className={`font-semibold text-sm ${
                        account.balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        €{account.balance.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              
              {trueLayerStatus.last_sync && (
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-2">
                  <Calendar className="w-3 h-3" />
                  Ultimo sync: {new Date(trueLayerStatus.last_sync).toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              )}
            </div>
          )}

          {/* Action buttons */}
          {trueLayerStatus?.connected ? (
            <div className="flex gap-2">
              <button
                onClick={handleTrueLayerSync}
                disabled={isTrueLayerSyncing}
                className="flex-1 btn btn-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isTrueLayerSyncing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Sincronizza
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Vuoi disconnettere il conto bancario?')) {
                    disconnectTrueLayerMutation.mutate()
                  }
                }}
                disabled={disconnectTrueLayerMutation.isPending}
                className="btn btn-sm bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/50 dark:hover:bg-red-900 dark:text-red-400"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleTrueLayerConnect}
              disabled={isTrueLayerConnecting}
              className="w-full btn btn-primary disabled:opacity-50"
            >
              {isTrueLayerConnecting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Connessione...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4" />
                  Collega Conto Bancario
                </>
              )}
            </button>
          )}
        </div>

        <div className="card border-dashed opacity-60 cursor-not-allowed">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" fill="#217346"/>
                <path d="M12.5 17h-5v-1.5h5V17zm0-3h-5v-1.5h5V14zm4.5 3h-3v-1.5h3V17zm0-3h-3v-1.5h3V14zm0-3h-8V9.5h8V11z" fill="white"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-500 dark:text-gray-400">Excel Online</h3>
                <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded-full">
                  Prossimamente
                </span>
              </div>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Importa fogli di calcolo da OneDrive
              </p>
            </div>
          </div>
        </div>

      </div>

      {/* Help Section */}
      <div className="card bg-blue-50 dark:bg-blue-900/30 border-blue-100 dark:border-blue-800">
        <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2">
          💡 Come funzionano le integrazioni?
        </h4>
        <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
          <li>• Collega i tuoi account esterni per importare automaticamente i dati finanziari</li>
          <li>• I dati vengono sincronizzati nel workspace corrente</li>
          <li>• Puoi sincronizzare manualmente o impostare sincronizzazioni automatiche</li>
          <li>• Le credenziali sono gestite in modo sicuro tramite OAuth 2.0</li>
        </ul>
      </div>
    </div>
  )
}
